import React, { useState, useMemo } from 'react';
import { Box, Typography, TextField, InputAdornment, IconButton } from '@mui/material';
import { Button, EmptyState } from './ui';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * Tile-based multi-select filter (max 4 items) with local search.
 */
export default function FilterTileGroup({
  title,
  items,
  selectedIds,
  getItemId,
  getItemLabel,
  onToggle,
  onApply,
  isActive,
  loading,
  maxSelection = 4,
  emptyMessage = 'No items available',
  showApplyButton = true,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const selectedCount = selectedIds.length;
  const atLimit = selectedCount >= maxSelection;

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      getItemLabel(item).toLowerCase().includes(query)
    );
  }, [items, searchQuery, getItemLabel]);

  return (
    <Box
      sx={{
        height: '100%',
        border: isActive ? '2px solid var(--color-primary-main)' : '1px solid var(--color-border-default)',
        backgroundColor: isActive ? 'var(--color-primary-50)' : 'var(--color-bg-paper)',
        borderRadius: 'var(--radius-lg)',
        p: 2,
        transition: 'all var(--transition-normal)',
        boxShadow: isActive ? 'var(--shadow-md)' : 'var(--shadow-xs)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select up to {maxSelection} · {selectedCount}/{maxSelection} selected
          </Typography>
        </Box>
        {showApplyButton && (
          <Button
            variant={isActive ? 'primary' : 'outlined'}
            size="small"
            onClick={onApply}
            disabled={loading || selectedCount === 0}
            loading={loading}
            sx={{ minWidth: 80, flexShrink: 0 }}
          >
            Apply
          </Button>
        )}
      </Box>

      <TextField
        size="small"
        fullWidth
        placeholder={`Filter ${title.toLowerCase().replace(' filter', '')}...`}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          endAdornment: searchQuery ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear filter"
                onClick={() => setSearchQuery('')}
                edge="end"
              >
                <ClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          mb: 1.5,
          '& .MuiOutlinedInput-root': {
            fontSize: 'var(--font-size-sm)',
            backgroundColor: 'var(--color-bg-paper)',
          },
        }}
      />

      {items.length === 0 ? (
        <EmptyState
          compact
          icon={null}
          description={emptyMessage}
          sx={{ py: 2 }}
        />
      ) : filteredItems.length === 0 ? (
        <EmptyState
          compact
          icon={null}
          description={`No matches for "${searchQuery}"`}
          sx={{ py: 2 }}
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gridAutoRows: '40px',
            gap: 1,
            flex: 1,
            minHeight: 100,
            maxHeight: 140,
            overflowY: 'auto',
            pr: 0.5,
            alignItems: 'stretch',
          }}
        >
          {filteredItems.map((item) => {
            const id = getItemId(item);
            const label = getItemLabel(item);
            const isSelected = selectedIds.includes(id);
            const isDisabled = !isSelected && atLimit;

            return (
              <Box
                key={id}
                component="button"
                type="button"
                onClick={() => !isDisabled && onToggle(item)}
                disabled={isDisabled}
                title={isDisabled ? `Maximum ${maxSelection} selections reached` : label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.5,
                  px: 1.5,
                  height: 40,
                  minHeight: 40,
                  maxHeight: 40,
                  boxSizing: 'border-box',
                  border: '2px solid',
                  borderColor: isSelected
                    ? 'var(--color-primary-main)'
                    : 'var(--color-border-default)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: isSelected ? 'var(--color-bg-paper)' : 'var(--color-bg-subtle)',
                  color: isSelected ? 'var(--color-primary-main)' : 'var(--color-text-primary)',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.45 : 1,
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: isSelected ? 600 : 500,
                  lineHeight: 1.3,
                  transition: 'all var(--transition-fast)',
                  '&:hover': isDisabled
                    ? {}
                    : {
                        borderColor: 'var(--color-primary-main)',
                        backgroundColor: isSelected ? 'var(--color-bg-paper)' : 'var(--color-primary-50)',
                      },
                }}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {label}
                </span>
                <CheckCircleIcon
                  sx={{
                    fontSize: 16,
                    flexShrink: 0,
                    width: 16,
                    height: 16,
                    color: 'var(--color-primary-main)',
                    visibility: isSelected ? 'visible' : 'hidden',
                  }}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
